// PipelineFavicon — swap the document favicon to reflect the live
// pipeline status (sharp at any DPR, glyph from the nerd font; served
// by PipelineFaviconController).
//
// The hook used to capture the original favicon links per-instance and
// re-attach them on destroyed(). On LV navigation the new page's
// mounted() can fire BEFORE the previous page's destroyed(), so the late
// snapshot would re-attach stale originals and silently wipe the new
// pipeline favicon. The fix is a module-scope ref count + single source
// of truth for originals.
//
// We do NOT animate in-flight statuses. Browser favicon repaint is
// throttled (~5–10 Hz, varies by browser/load), so the resulting motion
// is too chunky to read as "in progress" — a static glyph reads better
// than a janky one.

import type { LiveViewHook } from "./types";
import { validPipelineStatus, type PipelineStatus } from "../../../.dox/ci";

interface PipelineFaviconState {
  _currentStatus: PipelineStatus | null;
}

interface PipelineFaviconGlobal {
  originals: HTMLLinkElement[] | null;
  mountCount: number;
}

const PIPELINE_FAVICON_GLOBAL_KEY = "__operationPipelineFaviconState";

export const pipelineFaviconState = (): PipelineFaviconGlobal => {
  const w = window as typeof window & {
    [PIPELINE_FAVICON_GLOBAL_KEY]?: PipelineFaviconGlobal;
  };
  if (!w[PIPELINE_FAVICON_GLOBAL_KEY]) {
    w[PIPELINE_FAVICON_GLOBAL_KEY] = { originals: null, mountCount: 0 };
  }
  return w[PIPELINE_FAVICON_GLOBAL_KEY];
};

export const removePipelineFaviconLinks = (): void => {
  document
    .querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"][data-pipeline-favicon], link[rel="shortcut icon"][data-pipeline-favicon]',
    )
    .forEach((el) => {
      el.remove();
    });
};

// Build the favicon link with rel + type + href set BEFORE appending.
// Appending an empty <link rel="icon"> and then setting href later doesn't
// reliably trigger a favicon refresh in Chrome — the tab strip stays on
// whatever it had, including the brand favicon we just removed.
export const installPipelineFavicon = (status: PipelineStatus): void => {
  const link = document.createElement("link");
  link.rel = "icon";
  link.type = "image/svg+xml";
  link.href = `/favicon-pipeline.svg?status=${encodeURIComponent(status)}`;
  link.dataset["pipelineFavicon"] = "1";
  removePipelineFaviconLinks();
  document.head.appendChild(link);
};

export const acquirePipelineFavicon = (status: PipelineStatus): void => {
  const state = pipelineFaviconState();
  if (state.mountCount === 0) {
    state.originals = Array.from(
      document.querySelectorAll<HTMLLinkElement>(
        'link[rel="icon"]:not([data-pipeline-favicon]), link[rel="shortcut icon"]:not([data-pipeline-favicon])',
      ),
    );
    state.originals.forEach((el) => {
      el.remove();
    });
  }
  state.mountCount += 1;
  installPipelineFavicon(status);
};

export const releasePipelineFavicon = (): void => {
  const state = pipelineFaviconState();
  state.mountCount = Math.max(0, state.mountCount - 1);
  if (state.mountCount === 0) {
    removePipelineFaviconLinks();
    if (state.originals) {
      state.originals.forEach((el) => document.head.appendChild(el));
      state.originals = null;
    }
  }
};

// Read + validate the dataset value at the DOM boundary so a typo'd
// server-rendered status surfaces here (`Invalid PipelineStatus
// \`xxx\``) instead of silently being passed to the favicon endpoint
// as an unknown query-string value.
const readStatus = (el: HTMLElement): PipelineStatus =>
  validPipelineStatus(el.dataset["status"]);

export const PipelineFavicon = {
  mounted(this: LiveViewHook<never> & PipelineFaviconState) {
    this._currentStatus = readStatus(this.el);
    acquirePipelineFavicon(this._currentStatus);
  },
  updated(this: LiveViewHook<never> & PipelineFaviconState) {
    const next = readStatus(this.el);
    if (next !== this._currentStatus) {
      this._currentStatus = next;
      installPipelineFavicon(next);
    }
  },
  destroyed(this: LiveViewHook<never> & PipelineFaviconState) {
    releasePipelineFavicon();
  },
};
